import { useState, useEffect } from 'react';
import { Link } from '@lmig/lmds-react-link';
import './PolicyLookup.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { Heading } from '@lmig/lmds-react-typography';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { getBillingURLByAccount } from 'Utilities/URLHelper';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import AccessDeniedMessage from './Helpers/AccessDeniedMessage';

function BillingSummary(props: { policyNumber: string; callback: () => void }) {
  const [billing, setBilling] = useState<{ data?: IBillingLookupResults[] | null; isLoaded: boolean }>({
    isLoaded: false,
  });
  const [sending, setSending] = useState<string>();
  const [responseStatus, setResponseStatus] = useState<{ statusCode: number; message: string }>({
    statusCode: HttpStatusCodes.InternalServerError,
    message: '',
  });
  const { userData } = useUserData();
  const defaultMessage = 'No billing activity found for this customer for the last 45 days.';

  useEffect(() => {
    const fetchData = async () => {
      setBilling({ isLoaded: false });
      let response = (await SitecoreFetch(
        `/api/sitecore/BillingAsync/GetBillingLookupResults?agencyNumber=${userData?.selectedStatCodeData?.SubStat}&policyNumber=${props.policyNumber}`,
      )) as IServerResponse;
      setResponseStatus({ statusCode: response.Status.Code, message: response.Status.Message });
      let result: IBillingLookupResults[] | null;
      if (response.Status.Code === HttpStatusCodes.OK) result = response.Data?.reverse() as IBillingLookupResults[];
      else {
        result = null;
      }
      setBilling({ data: result, isLoaded: true });
      props.callback();
    };

    try {
      fetchData();
    } catch (error) {
      console.error(error);
    }
  }, [props.policyNumber, userData?.selectedStatCodeData?.SubStat]);

  async function openBilling(accountNumber: string) {
    if (sending && sending == accountNumber) return;
    setSending(accountNumber);
    try {
      if (userData && billing.data && accountNumber) {
        let url: string = await getBillingURLByAccount(userData, accountNumber);
        setSending(undefined);
        if (url) window.open(url, '', 'width=1000,height=800,left=0,top=50');
      }
    } catch {
      setSending(undefined);
    }
  }

  const isDataInvalid = (): boolean => {
    try {
      return (
        billing?.data?.length == 0 ||
        !billing.data ||
        billing.data.find(
          (x) =>
            x.AccountNumber == null &&
            x.AccountBalance == null &&
            x.MinimumDue == null &&
            x.DueDate == null &&
            x.LastPaymentAmount == null,
        ) != undefined
      );
    } catch (e) {
      return true;
    }
  };

  return (
    <>
      {userData && (
        <div>
          <Heading type="h4-light">Billing Summary</Heading>
          {!billing.isLoaded ? (
            <CircularProgressSpinner size="32" aria-label="Loading policy data..." />
          ) : !isDataInvalid() ? (
            billing.data?.map((result) => {
              return (
                <>
                  <div className="billing-row">
                    <span>
                      <div className="billing-label">Account #</div>
                      {sending && sending == result.AccountNumber ? (
                        <CircularProgressSpinner
                          size="16"
                          aria-label="Loading billing application..."
                          style={{ margin: '0 0 0 1.5rem' }}
                        />
                      ) : (
                        <Link onClick={() => openBilling(result.AccountNumber)}>{result.AccountNumber}</Link>
                      )}
                    </span>
                    <span>
                      <div className="billing-label">Payment Plan</div>
                      {result.PaymentPlan}
                    </span>
                    <span>
                      <div className="billing-label">Account Balance</div>
                      {result.AccountBalance}
                    </span>
                    <span>
                      <div className="billing-label">Minimum Due</div>
                      {result.MinimumDue}
                    </span>
                    <span>
                      <div className="billing-label">Due Date</div>
                      {result.DueDate}
                    </span>
                  </div>
                  <div className="billing-table">
                    <div className="billing-column">
                      <div>
                        <span className="billing-label-inline">Last Payment Amount</span>
                        <span>{result.LastPaymentAmount}</span>
                      </div>
                      <div>
                        <span className="billing-label-inline">Last Billing Activity</span>
                        <span>{result.LastBillingActivity}</span>
                      </div>
                      <div>
                        <span className="billing-label-inline">Next Billing Activity</span>
                        <span>{result.NextBillingActivity}</span>
                      </div>
                    </div>
                    <div className="billing-column">
                      <div>
                        <span className="billing-label-inline">Last Payment Date</span>
                        <span>{result.LastPaymentDate}</span>
                      </div>
                      <div>
                        <span className="billing-label-inline">Last Billing Activity Date</span>
                        <span>{result.LastBillingActivityDate}</span>
                      </div>
                      <div>
                        <span className="billing-label-inline">Next Billing Activity Date</span>
                        <span>{result.NextBillingActivityDate}</span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })
          ) : responseStatus.statusCode === HttpStatusCodes.OK ? (
            defaultMessage
          ) : (
            <AccessDeniedMessage
              status={responseStatus.statusCode}
              responseMessage={responseStatus.message}
              activityDataType={'billing'}
            ></AccessDeniedMessage>
          )}
        </div>
      )}
    </>
  );
}

export default BillingSummary;
